import dayjs from 'dayjs';
import { DataGridIdentifiers } from '../components/Interface';
import { DocStatus, InvoiceModificationScope } from '../config/document';
import { DocumentType } from '../config/DocumentType';
import { v4 as uuidv4 } from 'uuid';
import addNotification from './addNotification';
import { NotificationType } from '../config';
import { DocumentDirection } from '../config/DocumentDirection';
import { PriceFormField } from '../config/priceList';

const PURCHASE_ORDER_EMPTY_HEADER_FIELDS = {
  companyId: null,
  companyName: null,
  companyCode: null,
  companyContactId: null,
  currencyCode: null,
  paymentMethodId: null,
  billingAdressId: null,
  billingCountryCode: null,
  billingCountry: null,
  billingProvince: null,
  billingPostCode: null,
  billingCity: null,
  billingStreetAddress: null,
  shippingAdressId: null,
  shippingCountryCode: null,
  shippingCountry: null,
  shippingProvince: null,
  shippingPostCode: null,
  shippingCity: null,
  shippingStreetAddress: null,
  deliverymethodId: null,
  incotermCode: null,
  priceListId: null,
  langCode: null,
  hunTaxNumber: null,
  euTaxNumber: null,
  foreignTaxNumber: null,
  vatGroupNumber: null,
  customerTaxId: null,
  customerTaxIdType: null,
  groupMemberTaxNumber: null,
  incotermCity: null,
  companyWarning: null,
  shortName: null,
};

const PURCHASE_ORDER_EMPTY_LINE_FIELDS = {
  unitPrice: null,
  vatCode: null,
  netPrice: null,
  grossPrice: null,
  grossUnitPrice: null,
  priceUnitPrice: null,
  priceCurrencyCode: null,
  priceExchangeRate: null,
  priceForm: PriceFormField.NET,
  baseUnitPrice: null,
  baseGrossUnitPrice: null,
  vatValueInOwnCur: null,
  netPriceInOwnCur: null,
  grossPriceInOwnCur: null,
  vatValue: null,
  vatPercent: null,
};

export const getDocDataGridIdentifiers = (docTypeId) => {
  switch (parseInt(docTypeId)) {
    case DocumentType.SalesQuotation:
      return {
        doc: DataGridIdentifiers.SalesQuotationDoc,
        docLine: DataGridIdentifiers.SalesQuotationDocLine,
      };
    case DocumentType.CustomerOrder:
      return {
        doc: DataGridIdentifiers.CustomerOrderDoc,
        docLine: DataGridIdentifiers.CustomerOrderDocLine,
      };
    case DocumentType.Delivery:
      return {
        doc: DataGridIdentifiers.DeliveryNoteDoc,
        docLine: DataGridIdentifiers.DeliveryNoteDocLine,
      };
    case DocumentType.CompletionCertificate:
      return {
        doc: DataGridIdentifiers.CompletionCertificateDoc,
        docLine: DataGridIdentifiers.CompletionCertificateDocLine,
      };
    case DocumentType.Return:
      return {
        doc: DataGridIdentifiers.ReturnDoc,
        docLine: DataGridIdentifiers.ReturnDocLine,
      };
    case DocumentType.DownPaymentRequest:
      return {
        doc: DataGridIdentifiers.DownPaymentRequestDoc,
        docLine: DataGridIdentifiers.DownPaymentRequestDocLine,
      };
    case DocumentType.DownPayment:
      return {
        doc: DataGridIdentifiers.DownPaymentDoc,
        docLine: DataGridIdentifiers.DownPaymentDocLine,
      };
    case DocumentType.ARInvoiceManual:
    case DocumentType.ARInvoice:
    case DocumentType.ARInvoiceModification:
      return {
        doc: DataGridIdentifiers.ARInvoiceDoc,
        docLine: DataGridIdentifiers.ARInvoiceDocLine,
      };
    case DocumentType.PurchaseRequest:
      return {
        doc: DataGridIdentifiers.PurchaseRequestDoc,
        docLine: DataGridIdentifiers.PurchaseRequestDocLine,
      };
    case DocumentType.PurchaseQuotation:
      return {
        doc: DataGridIdentifiers.PurchaseQuotationDoc,
        docLine: DataGridIdentifiers.PurchaseQuotationDocLine,
      };
    case DocumentType.PurchaseOrder:
      return {
        doc: DataGridIdentifiers.PurchaseOrderDoc,
        docLine: DataGridIdentifiers.PurchaseOrderDocLine,
      };
    case DocumentType.PurchaseDelivery:
      return {
        doc: DataGridIdentifiers.PurchaseDeliveryNoteDoc,
        docLine: DataGridIdentifiers.PurchaseDeliveryNoteDocLine,
      };
    case DocumentType.GoodsReturn:
      return {
        doc: DataGridIdentifiers.GoodsReturnDoc,
        docLine: DataGridIdentifiers.GoodsReturnDocLine,
      };
    case DocumentType.APDownPaymentRequest:
      return {
        doc: DataGridIdentifiers.APDownPaymentRequestDoc,
        docLine: DataGridIdentifiers.APDownPaymentRequestDocLine,
      };
    case DocumentType.APDownPayment:
      return {
        doc: DataGridIdentifiers.APDownPaymentDoc,
        docLine: DataGridIdentifiers.APDownPaymentDocLine,
      };
    case DocumentType.APInvoice:
      return {
        doc: DataGridIdentifiers.APInvoiceDoc,
        docLine: DataGridIdentifiers.APInvoiceDocLine,
      };
    case DocumentType.PurchaseOrderVariant:
      return {
        doc: DataGridIdentifiers.PurchaseOrderVariantDoc,
        docLine: DataGridIdentifiers.PurchaseOrderVariantDocLine,
      };
    case DocumentType.InventoryTransfer:
      return {
        doc: DataGridIdentifiers.InventoryTransferDoc,
        docLine: DataGridIdentifiers.InventoryTransferDocLine,
      };
    case DocumentType.GoodsReceipt:
    case DocumentType.ConversionReceipt:
      return {
        doc: DataGridIdentifiers.GoodsReceiptDoc,
        docLine: DataGridIdentifiers.GoodsReceiptDocLine,
      };
    case DocumentType.GoodsIssue:
    case DocumentType.ConversionIssue:
      return {
        doc: DataGridIdentifiers.GoodsIssueDoc,
        docLine: DataGridIdentifiers.GoodsIssueDocLine,
      };
    case DocumentType.ProjectIssue:
      return {
        doc: DataGridIdentifiers.ProjectIssueDoc,
        docLine: DataGridIdentifiers.ProjectIssueDocLine,
      };
    case DocumentType.CooperationIssue:
      return {
        doc: DataGridIdentifiers.CooperationIssueDoc,
        docLine: DataGridIdentifiers.CooperationIssueDocLine,
      };
    case DocumentType.EmployeeIssue:
      return {
        doc: DataGridIdentifiers.EmployeeIssueDoc,
        docLine: DataGridIdentifiers.EmployeeIssueDocLine,
      };
    case DocumentType.InventoryCounting:
      return {
        doc: DataGridIdentifiers.InventoryCountingDoc,
        docLine: DataGridIdentifiers.InventoryCountingDocLine,
      };
    case DocumentType.InventoryCountingDif:
      return {
        doc: DataGridIdentifiers.InventoryCountingDifDoc,
        docLine: DataGridIdentifiers.InventoryCountingDifDocLine,
      };
    case DocumentType.ReceiptFromProduction:
      return {
        doc: DataGridIdentifiers.ReceiptFromProductionDoc,
        docLine: DataGridIdentifiers.ReceiptFromProductionDocLine,
      };
    case DocumentType.IssueForProduction:
      return {
        doc: DataGridIdentifiers.IssueForProductionDoc,
        docLine: DataGridIdentifiers.IssueForProductionDocLine,
      };
    case DocumentType.ProductionOrder:
      return {
        doc: DataGridIdentifiers.ProductionOrderDoc,
        docLine: DataGridIdentifiers.ProductionOrderDocLine,
      };
    case DocumentType.IssueForService:
      return {
        doc: DataGridIdentifiers.IssueForServiceDoc,
        docLine: DataGridIdentifiers.IssueForServiceDocLine,
      };
    case DocumentType.ReceiptFromService:
      return {
        doc: DataGridIdentifiers.ReceiptFromServiceDoc,
        docLine: DataGridIdentifiers.ReceiptFromServiceDocLine,
      };
    case DocumentType.ReturnFromProduction:
      return {
        doc: DataGridIdentifiers.ReturnFromProductionDoc,
        docLine: DataGridIdentifiers.ReturnFromProductionDocLine,
      };
    case DocumentType.ReturnFromIssue:
      return {
        doc: DataGridIdentifiers.ReturnFromIssueDoc,
        docLine: DataGridIdentifiers.ReturnFromIssueDocLine,
      };
    case DocumentType.ProjectReturn:
      return {
        doc: DataGridIdentifiers.ProjectReturnDoc,
        docLine: DataGridIdentifiers.ProjectReturnDocLine,
      };
    case DocumentType.CooperationReturn:
      return {
        doc: DataGridIdentifiers.CooperationReturnDoc,
        docLine: DataGridIdentifiers.CooperationReturnDocLine,
      };
    case DocumentType.EmployeeReturn:
      return {
        doc: DataGridIdentifiers.EmployeeReturnDoc,
        docLine: DataGridIdentifiers.EmployeeReturnDocLine,
      };
    default:
      return DataGridIdentifiers.documentReports;
  }
};

export const getSourceDocDataGridIdentifiers = (docTypeId) => {
  switch (parseInt(docTypeId)) {
    case DocumentType.SalesQuotation:
      return {
        sourceDoc: DataGridIdentifiers.SourceSalesQuotationDoc,
        sourceDocLine: DataGridIdentifiers.SourceSalesQuotationDocLine,
      };
    case DocumentType.CustomerOrder:
      return {
        sourceDoc: DataGridIdentifiers.SourceCustomerOrderDoc,
        sourceDocLine: DataGridIdentifiers.SourceCustomerOrderDocLine,
      };
    case DocumentType.Delivery:
      return {
        sourceDoc: DataGridIdentifiers.SourceDeliveryNoteDoc,
        sourceDocLine: DataGridIdentifiers.SourceDeliveryNoteDocLine,
      };
    case DocumentType.Return:
      return {
        sourceDoc: DataGridIdentifiers.SourceReturnDoc,
        sourceDocLine: DataGridIdentifiers.SourceReturnDocLine,
      };
    case DocumentType.DownPaymentRequest:
      return {
        sourceDoc: DataGridIdentifiers.SourceDownPaymentRequestDoc,
        sourceDocLine: DataGridIdentifiers.SourceDownPaymentRequestDocLine,
      };
    case DocumentType.DownPayment:
      return {
        sourceDoc: DataGridIdentifiers.SourceDownPaymentDoc,
        sourceDocLine: DataGridIdentifiers.SourceDownPaymentDocLine,
      };
    case DocumentType.ARInvoiceManual:
    case DocumentType.ARInvoice:
    case DocumentType.ARInvoiceModification:
      return {
        sourceDoc: DataGridIdentifiers.SourceARInvoiceDoc,
        sourceDocLine: DataGridIdentifiers.SourceARInvoiceDocLine,
      };
    case DocumentType.PurchaseRequest:
      return {
        sourceDoc: DataGridIdentifiers.SourcePurchaseRequestDoc,
        sourceDocLine: DataGridIdentifiers.SourcePurchaseRequestDocLine,
      };
    case DocumentType.PurchaseQuotation:
      return {
        sourceDoc: DataGridIdentifiers.SourcePurchaseQuotationDoc,
        sourceDocLine: DataGridIdentifiers.SourcePurchaseQuotationDocLine,
      };
    case DocumentType.PurchaseOrder:
      return {
        sourceDoc: DataGridIdentifiers.SourcePurchaseOrderDoc,
        sourceDocLine: DataGridIdentifiers.SourcePurchaseOrderDocLine,
      };
    case DocumentType.PurchaseDelivery:
      return {
        sourceDoc: DataGridIdentifiers.SourcePurchaseDeliveryNoteDoc,
        sourceDocLine: DataGridIdentifiers.SourcePurchaseDeliveryNoteDocLine,
      };
    case DocumentType.GoodsReturn:
      return {
        sourceDoc: DataGridIdentifiers.SourceGoodsReturnDoc,
        sourceDocLine: DataGridIdentifiers.SourceGoodsReturnDocLine,
      };
    case DocumentType.APDownPaymentRequest:
      return {
        sourceDoc: DataGridIdentifiers.SourceAPDownPaymentRequestDoc,
        sourceDocLine: DataGridIdentifiers.SourceAPDownPaymentRequestDocLine,
      };
    case DocumentType.APDownPayment:
      return {
        sourceDoc: DataGridIdentifiers.SourceAPDownPaymentDoc,
        sourceDocLine: DataGridIdentifiers.SourceAPDownPaymentDocLine,
      };
    case DocumentType.APInvoice:
      return {
        sourceDoc: DataGridIdentifiers.SourceAPInvoiceDoc,
        sourceDocLine: DataGridIdentifiers.SourceAPInvoiceDocLine,
      };
    case DocumentType.PurchaseOrderVariant:
      return {
        sourceDoc: DataGridIdentifiers.SourcePurchaseOrderVariantDoc,
        sourceDocLine: DataGridIdentifiers.SourcePurchaseOrderVariantDocLine,
      };
    case DocumentType.InventoryTransfer:
      return {
        sourceDoc: DataGridIdentifiers.SourceInventoryTransferDoc,
        sourceDocLine: DataGridIdentifiers.SourceInventoryTransferDocLine,
      };
    case DocumentType.GoodsReceipt:
    case DocumentType.ConversionReceipt:
      return {
        sourceDoc: DataGridIdentifiers.SourceGoodsReceiptDoc,
        sourceDocLine: DataGridIdentifiers.SourceGoodsReceiptDocLine,
      };
    case DocumentType.GoodsIssue:
    case DocumentType.ConversionIssue:
      return {
        sourceDoc: DataGridIdentifiers.SourceGoodsIssueDoc,
        sourceDocLine: DataGridIdentifiers.SourceGoodsIssueDocLine,
      };
    case DocumentType.ProjectIssue:
      return {
        sourceDoc: DataGridIdentifiers.SourceProjectIssueDoc,
        sourceDocLine: DataGridIdentifiers.SourceProjectIssueDocLine,
      };
    case DocumentType.CooperationIssue:
      return {
        sourceDoc: DataGridIdentifiers.SourceCooperationIssueDoc,
        sourceDocLine: DataGridIdentifiers.SourceCooperationIssueDocLine,
      };
    case DocumentType.EmployeeIssue:
      return {
        sourceDoc: DataGridIdentifiers.SourceEmployeeIssueDoc,
        sourceDocLine: DataGridIdentifiers.SourceEmployeeIssueDocLine,
      };
    case DocumentType.InventoryCounting:
      return {
        sourceDoc: DataGridIdentifiers.SourceInventoryCountingDoc,
        sourceDocLine: DataGridIdentifiers.SourceInventoryCountingDocLine,
      };
    case DocumentType.InventoryCountingDif:
      return {
        sourceDoc: DataGridIdentifiers.SourceInventoryCountingDifDoc,
        sourceDocLine: DataGridIdentifiers.SourceInventoryCountingDifDocLine,
      };
    case DocumentType.ReceiptFromProduction:
      return {
        sourceDoc: DataGridIdentifiers.SourceReceiptFromProductionDoc,
        sourceDocLine: DataGridIdentifiers.SourceReceiptFromProductionDocLine,
      };
    case DocumentType.IssueForProduction:
      return {
        sourceDoc: DataGridIdentifiers.SourceIssueForProductionDoc,
        sourceDocLine: DataGridIdentifiers.SourceIssueForProductionDocLine,
      };
    case DocumentType.ProductionOrder:
      return {
        sourceDoc: DataGridIdentifiers.SourceProductionOrderDoc,
        sourceDocLine: DataGridIdentifiers.SourceProductionOrderDocLine,
      };
    case DocumentType.IssueForService:
      return {
        sourceDoc: DataGridIdentifiers.SourceIssueForServiceDoc,
        sourceDocLine: DataGridIdentifiers.SourceIssueForServiceDocLine,
      };
    case DocumentType.ReceiptFromService:
      return {
        sourceDoc: DataGridIdentifiers.SourceReceiptFromServiceDoc,
        sourceDocLine: DataGridIdentifiers.SourceReceiptFromServiceDocLine,
      };
    case DocumentType.ReturnFromProduction:
      return {
        sourceDoc: DataGridIdentifiers.SourceReturnFromProductionDoc,
        sourceDocLine: DataGridIdentifiers.SourceReturnFromProductionDocLine,
      };
    case DocumentType.ReturnFromIssue:
      return {
        sourceDoc: DataGridIdentifiers.SourceReturnFromIssueDoc,
        sourceDocLine: DataGridIdentifiers.SourceReturnFromIssueDocLine,
      };
    case DocumentType.ProjectReturn:
      return {
        sourceDoc: DataGridIdentifiers.SourceProjectReturnDoc,
        sourceDocLine: DataGridIdentifiers.SourceProjectReturnDocLine,
      };
    case DocumentType.CooperationReturn:
      return {
        sourceDoc: DataGridIdentifiers.SourceCooperationReturnDoc,
        sourceDocLine: DataGridIdentifiers.SourceCooperationReturnDocLine,
      };
    case DocumentType.EmployeeReturn:
      return {
        sourceDoc: DataGridIdentifiers.SourceEmployeeReturnDoc,
        sourceDocLine: DataGridIdentifiers.SourceEmployeeReturnDocLine,
      };
    default:
      return DataGridIdentifiers.documentReports;
  }
};

export const getDocumentTypeKeyUpperCase = (value) => {
  return Object.entries(DocumentType)
    .find(([_, v]) => v === parseInt(value))?.[0]
    ?.toUpperCase();
};

export const extractCopyPayload = (
  selectedValues,
  newDocType = null,
  defVals = {},
  docName = '',
  currDocTypeId = null,
  {
    includeClosedLinesForStorno = false,
    includeClosedLinesForReturn = false,
    useSelectedDocumentAsBase = false,
  } = {}
) => {
  const nowIso = dayjs().toISOString();
  const resolvedCompanyCode = selectedValues?.companyCode ?? selectedValues?.company?.code ?? '';
  const resolvedCompanyName = selectedValues?.companyName ?? selectedValues?.company?.name ?? '';
  const sourceDocumentTypeId = parseInt(currDocTypeId ?? selectedValues?.docTypeId);

  const shouldClearPurchaseOrderFields =
    (parseInt(newDocType) === DocumentType.PurchaseOrder ||
      parseInt(newDocType) === DocumentType.PurchaseOrderVariant) &&
    sourceDocumentTypeId === DocumentType.CustomerOrder;
  const shouldPreserveDeliveryDate =
    sourceDocumentTypeId === DocumentType.Delivery &&
    [DocumentType.ARInvoiceManual, DocumentType.ARInvoice].includes(parseInt(newDocType));
  const isInvoiceModification = parseInt(newDocType) === DocumentType.ARInvoiceModification;
  const isModificationContinuation = isInvoiceModification && sourceDocumentTypeId === DocumentType.ARInvoiceModification;
  if (isInvoiceModification && selectedValues.hasModification) {
    addNotification({
      content: 'Ehhez a számlához már készült módosító számla. A legutolsó módosító számlából indítsd a következő módosítást.',
      type: NotificationType.ERROR,
    });
    return;
  }
  if (isModificationContinuation && !Array.isArray(selectedValues.modificationStateLines)) {
    addNotification({
      content: 'A módosító számla utolsó állapotát előbb be kell tölteni.',
      type: NotificationType.ERROR,
    });
    return;
  }
  const isStorno = newDocType === null;
  const isInvoiceStorno = isStorno && [
    DocumentType.DownPayment, DocumentType.APDownPayment, DocumentType.ARInvoiceManual,
    DocumentType.ARInvoice, DocumentType.APInvoice,
  ].includes(sourceDocumentTypeId);
  const reverseStornoAmount = (value) => isInvoiceStorno ? negateAmount(value) : negativeAmount(value);
  const shouldInheritSettlement =
    Boolean(selectedValues?.balanceSettled) &&
    ((sourceDocumentTypeId === DocumentType.DownPaymentRequest &&
      parseInt(newDocType) === DocumentType.DownPayment) ||
      (sourceDocumentTypeId === DocumentType.APDownPaymentRequest &&
        parseInt(newDocType) === DocumentType.APDownPayment));
  const negateAmount = (value) => {
    if (value === null || value === undefined || value === '') return value;
    if (typeof value === 'number') return -value;
    const normalizedValue = String(value);
    return normalizedValue.startsWith('-') ? normalizedValue.slice(1) : `-${normalizedValue}`;
  };
  const negativeAmount = (value) => {
    if (value === null || value === undefined || value === '') return value;
    const amount = Number(value);
    return Number.isFinite(amount) ? -Math.abs(amount) : value;
  };
  const positiveAmount = (value) => {
    if (value === null || value === undefined || value === '') return value;
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.abs(amount) : value;
  };

  const baseHeader = {
    ...selectedValues,
    docNum: '',
    customStatus: null,
    customDocStatus: null,
    docDate: shouldPreserveDeliveryDate ? selectedValues.docDate : newDocType ? nowIso : selectedValues.docDate,
    issueDate: nowIso,
    dueDate: newDocType ? nowIso : selectedValues.dueDate,
    docStatus: newDocType ? DocStatus.OPENED : DocStatus.CANCELLATION,
    docTypeId: newDocType ?? selectedValues.docTypeId,
    mode: 'copy',
    refreshExchangeRateAfterCopy: Boolean(newDocType) && !isInvoiceModification,
    preserveCopiedDates: shouldPreserveDeliveryDate,
    note: [selectedValues.note, `Forrása: ${docName} ${selectedValues.docNum}`].filter(Boolean).join('\n'),
    balanceSettled: shouldInheritSettlement,
    settledAmount: shouldInheritSettlement ? selectedValues.settledAmount : null,
    openAmount: shouldInheritSettlement ? selectedValues.openAmount : null,
    companyCode: resolvedCompanyCode,
    companyName: resolvedCompanyName,
    settlement: [],
    preserveCopiedSettlement: shouldInheritSettlement,
    appliedGrossAmount: 0,

    ...(isStorno && {
      sumInDocCur: reverseStornoAmount(selectedValues.sumInDocCur),
      netInDocCur: reverseStornoAmount(selectedValues.netInDocCur),
      VATInDocCur: reverseStornoAmount(selectedValues.VATInDocCur),
      roundingInDocCur: negateAmount(selectedValues.roundingInDocCur),
      sumInOwnCur: reverseStornoAmount(selectedValues.sumInOwnCur),
      netInOwnCur: reverseStornoAmount(selectedValues.netInOwnCur),
      VATInOwnCur: reverseStornoAmount(selectedValues.VATInOwnCur),
      extraCostTotal: negativeAmount(selectedValues.extraCostTotal),
    }),

    ...(isInvoiceModification && {
      originalInvoiceId: isModificationContinuation ? selectedValues.originalInvoiceId : selectedValues.docId,
      originalInvoiceNumber: isModificationContinuation ? selectedValues.originalInvoiceNumber : selectedValues.docNum,
      originalInvoiceDate: isModificationContinuation ? selectedValues.originalInvoiceDate : selectedValues.docDate,
      modificationSourceInvoiceId: selectedValues.docId,
      modificationIndex: null,
      modificationIndexPreview: selectedValues.nextModificationIndex ?? null,
      modificationReason: '',
      modificationScope: InvoiceModificationScope.ALL,
      preserveCopiedDates: true,
      docDate: selectedValues.docDate,
      dueDate: selectedValues.dueDate,
      note: selectedValues.note ?? '',
    }),

    ...(shouldClearPurchaseOrderFields && PURCHASE_ORDER_EMPTY_HEADER_FIELDS),
  };
  delete baseHeader.finalizedAt;
  delete baseHeader.finalizedBy;
  delete baseHeader.supportingDocumentAssignment;
  delete baseHeader.modificationStateLines;
  delete baseHeader.hasModification;
  delete baseHeader.nextModificationIndex;
  delete baseHeader.navOnline;

  if ([DocumentType.DownPaymentRequest, DocumentType.APDownPaymentRequest].includes(parseInt(newDocType))) {
    if (!defVals.itemCode) {
      addNotification({
        content: 'Hiányzik az alapértelmezett cikk kódja!',
        type: NotificationType.ERROR,
      });
      return;
    }

    const toAmount = (value) => {
      const amount = Number(value);
      return Number.isFinite(amount) ? amount : 0;
    };
    const sourceLines = selectedValues?.docLine ?? [];
    const linesByVatCode = sourceLines.reduce((groups, line) => {
      const vatCode = line?.vatCode ?? defVals?.vatCode ?? null;
      const groupKey = vatCode ?? '';
      const current = groups.get(groupKey) ?? {
        vatCode,
        vatPercent: line?.vatPercent ?? defVals?.vatPercent ?? null,
        netPrice: 0,
        vatValue: 0,
        grossPrice: 0,
        netPriceInOwnCur: 0,
        vatValueInOwnCur: 0,
        grossPriceInOwnCur: 0,
      };

      current.netPrice += toAmount(line?.netPrice);
      current.vatValue += toAmount(line?.vatValue);
      current.grossPrice += toAmount(line?.grossPrice);
      current.netPriceInOwnCur += toAmount(line?.netPriceInOwnCur);
      current.vatValueInOwnCur += toAmount(line?.vatValueInOwnCur);
      current.grossPriceInOwnCur += toAmount(line?.grossPriceInOwnCur);
      groups.set(groupKey, current);
      return groups;
    }, new Map());

    if (linesByVatCode.size === 0) {
      const netPrice = toAmount(selectedValues?.netInDocCur);
      const vatValue = toAmount(selectedValues?.VATInDocCur);
      const grossPrice = toAmount(selectedValues?.sumInDocCur);
      linesByVatCode.set(defVals?.vatCode ?? '', {
        vatCode: defVals?.vatCode ?? null,
        vatPercent: defVals?.vatPercent ?? null,
        netPrice,
        vatValue,
        grossPrice,
        netPriceInOwnCur: toAmount(selectedValues?.netInOwnCur),
        vatValueInOwnCur: toAmount(selectedValues?.VATInOwnCur),
        grossPriceInOwnCur: toAmount(selectedValues?.sumInOwnCur),
      });
    }

    const downPaymentLines = Array.from(linesByVatCode.values()).map((amounts, index) => ({
      ...defVals,
      id: uuidv4(),
      isNew: true,
      resolveVatCodeAfterCopy: false,
      visOrder: index + 1,
      qty: 1,
      opnQty: 1,
      cplQty: 0,
      currencyCode: selectedValues?.currencyCode ?? defVals?.currencyCode,
      unitPrice: amounts.netPrice,
      grossUnitPrice: amounts.grossPrice,
      priceUnitPrice: amounts.netPrice,
      priceCurrencyCode: selectedValues?.currencyCode ?? defVals?.priceCurrencyCode,
      priceExchangeRate: 1,
      priceForm: PriceFormField.NET,
      baseUnitPrice: amounts.netPrice,
      baseGrossUnitPrice: amounts.grossPrice,
      vatPercent: amounts.vatPercent,
      vatCode: amounts.vatCode,
      netPrice: amounts.netPrice,
      vatValue: amounts.vatValue,
      grossPrice: amounts.grossPrice,
      netPriceInOwnCur: amounts.netPriceInOwnCur,
      vatValueInOwnCur: amounts.vatValueInOwnCur,
      grossPriceInOwnCur: amounts.grossPriceInOwnCur,
      appliedGrossAmount: 0,
      baseId: selectedValues.docId,
      baseDocnum: selectedValues.docNum,
      baseDocType: selectedValues.docTypeId,
      baseLine: null,
      dueDate: nowIso,
    }));

    return {
      ...baseHeader,
      docLine: downPaymentLines,
    };
  }

  const docLine = (isModificationContinuation ? selectedValues.modificationStateLines : selectedValues?.docLine || [])
    .filter((line) => {
      if (isInvoiceModification || isInvoiceStorno) {
        return [DocStatus.OPENED, DocStatus.CLOSED].includes(line?.lineStatus) && Number(line?.qty ?? 0) !== 0;
      }
      return includeClosedLinesForStorno || (includeClosedLinesForReturn && line?.lineStatus === DocStatus.CLOSED)
        ? line?.lineStatus === DocStatus.CLOSED && Number(line?.qty ?? 0) > 0
        : line?.lineStatus === DocStatus.OPENED && line?.opnQty > 0;
    })
    .map((line) => ({
      ...line,
      id: uuidv4(),
      isNew: true,
      resolveVatCodeAfterCopy: shouldClearPurchaseOrderFields,
      baseId: useSelectedDocumentAsBase ? selectedValues.docId : line.baseId ?? selectedValues.docId,
      baseDocnum: useSelectedDocumentAsBase ? selectedValues.docNum : line.baseDocnum ?? selectedValues.docNum,
      baseDocType: useSelectedDocumentAsBase ? selectedValues.docTypeId : line.baseDocType ?? selectedValues.docTypeId,
      baseLine: newDocType ? line.lineNum : line.baseLine,
      qty:
        isInvoiceModification ||
        includeClosedLinesForStorno ||
        (includeClosedLinesForReturn && line.lineStatus === DocStatus.CLOSED)
          ? line.qty
          : line.opnQty,
      opnQty: 0,
      cplQty: 0,
      appliedGrossAmount: 0,
      dueDate: nowIso,
      lineStatus: newDocType
        ? includeClosedLinesForReturn || isInvoiceModification
          ? DocStatus.OPENED
          : line.lineStatus
        : DocStatus.CANCELLATION,

      ...(shouldClearPurchaseOrderFields && PURCHASE_ORDER_EMPTY_LINE_FIELDS),

      ...(isStorno && {
        qty: reverseStornoAmount(
          isInvoiceStorno || includeClosedLinesForStorno || (includeClosedLinesForReturn && line.lineStatus === DocStatus.CLOSED)
            ? line.qty
            : line.opnQty
        ),
        unitPrice: positiveAmount(line.unitPrice),
        grossUnitPrice: positiveAmount(line.grossUnitPrice),
        priceUnitPrice: positiveAmount(line.priceUnitPrice),
        baseUnitPrice: positiveAmount(line.baseUnitPrice),
        baseGrossUnitPrice: positiveAmount(line.baseGrossUnitPrice),
        netPrice: reverseStornoAmount(line.netPrice),
        vatValue: reverseStornoAmount(line.vatValue),
        grossPrice: reverseStornoAmount(line.grossPrice),
        netPriceInOwnCur: reverseStornoAmount(line.netPriceInOwnCur),
        vatValueInOwnCur: reverseStornoAmount(line.vatValueInOwnCur),
        grossPriceInOwnCur: reverseStornoAmount(line.grossPriceInOwnCur),
        finalUnitCost: positiveAmount(line.finalUnitCost),
        finalLineValue: negativeAmount(line.finalLineValue),
        weight: negativeAmount(line.weight),
        docLineBatchSn: (line.docLineBatchSn ?? []).map((batch) => ({
          ...batch,
          quantity: negativeAmount(batch.quantity),
        })),
        docLineBin: (line.docLineBin ?? []).map((bin) => ({
          ...bin,
          quantity: negativeAmount(bin.quantity),
        })),
      }),
    }));

  const selectedItemCodes = new Set(docLine.map((line) => line.itemCode).filter(Boolean));
  const modificationDocLines = isInvoiceModification
    ? docLine.flatMap((line) => {
        const common = {
          ...line,
          isNew: false,
          baseId: selectedValues.docId,
          baseDocnum: selectedValues.docNum,
          baseDocType: selectedValues.docTypeId,
          baseLine: line.lineNum,
        };
        return [
          {
            ...common,
            id: uuidv4(),
            modificationLineRole: 'REVERSAL',
            deletable: false,
            qty: negateAmount(line.qty),
            opnQty: negateAmount(line.qty),
            netPrice: negateAmount(line.netPrice),
            vatValue: negateAmount(line.vatValue),
            grossPrice: negateAmount(line.grossPrice),
            netPriceInOwnCur: negateAmount(line.netPriceInOwnCur),
            vatValueInOwnCur: negateAmount(line.vatValueInOwnCur),
            grossPriceInOwnCur: negateAmount(line.grossPriceInOwnCur),
            weight: negateAmount(line.weight),
            docLineBatchSn: (line.docLineBatchSn ?? []).map((batch) => ({
              ...batch,
              id: uuidv4(),
              quantity: negateAmount(batch.quantity),
            })),
            docLineBin: (line.docLineBin ?? []).map((bin) => ({
              ...bin,
              id: uuidv4(),
              quantity: negateAmount(bin.quantity),
            })),
          },
          {
            ...common,
            id: uuidv4(),
            modificationLineRole: 'REPLACEMENT',
            deletable: true,
            docLineBatchSn: (line.docLineBatchSn ?? []).map((batch) => ({ ...batch, id: uuidv4() })),
            docLineBin: (line.docLineBin ?? []).map((bin) => ({ ...bin, id: uuidv4() })),
          },
        ];
      }).map((line, index) => ({ ...line, visOrder: index + 1 }))
    : docLine;

  return {
    ...baseHeader,
    ...(Array.isArray(selectedValues?.supportingDocumentAssignment)
      ? {
          supportingDocumentAssignment: selectedValues.supportingDocumentAssignment.filter((assignment) =>
            selectedItemCodes.has(assignment.itemCode)
          ),
        }
      : {}),
    docLine: modificationDocLines,
  };
};

export const isCellEditable = ({ currentDocType, selectedValues }) => {
  return ({ field, row }) => {
    const isInvoiceModification =
      Number(currentDocType?.documentTypeId) === DocumentType.ARInvoiceModification;
    if (isInvoiceModification) {
      if (selectedValues.modificationScope === InvoiceModificationScope.HEAD) return false;
      if (row?.modificationLineRole === 'REVERSAL') return false;
      if (field === 'itemCode' && row?.modificationLineRole === 'REPLACEMENT') return false;
    }
    if (
      field === 'vatCode' &&
      [DocumentType.DownPayment, DocumentType.APDownPayment].includes(Number(row?.baseDocType))
    )
      return false;
    if (selectedValues.docStatus === DocStatus.PLANNED) return true;
    if (field === 'itemName' && !currentDocType?.editableItemName) return false;
    if (field === 'whsCode' && currentDocType?.inventoryAccounting && !row?.isNew && !isInvoiceModification) return false;
    if (
      field === 'qty' &&
      currentDocType.inventoryAccounting &&
      !row?.isNew &&
      selectedValues.mode !== 'copy' &&
      !isInvoiceModification
    )
      return false;
    if (field === 'itemCode' && !row?.isNew) return false;
    if (field === 'netPrice' && currentDocType?.direction !== DocumentDirection.INCOMING) {
      return false;
    }
    if (field === 'toWhsCode' && currentDocType?.direction !== DocumentDirection.INTERNAL) return false;
    if (field === 'toBinId' && currentDocType?.direction !== DocumentDirection.INTERNAL) return false;

    return true;
  };
};

export const getLineEditableFields = (apiRef, rowId, { currentDocType, selectedValues }) => {
  const allCols = apiRef.current.getVisibleColumns?.() ?? [];
  const row = apiRef.current.getRow?.(rowId);
  const canEdit = isCellEditable({ currentDocType, selectedValues });

  const editableFields = allCols
    .filter((c) => c.type !== 'actions' && c.editable)
    .filter((c) => {
      return canEdit({ field: c.field, row });
    })
    .map((c) => c.field);

  return editableFields;
};
